<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import AppIcon from '@/components/ui/AppIcon.vue'
import { haptic } from '@/composables/useTelegram'
import { ROUTE } from '@/constants/routes'
import { useChatStore } from '@/stores/chat'

/**
 * Алерт об ответе поддержки. Висит поверх любого экрана и ведёт прямо
 * в переписку: сообщение приходит, когда клиент занят меню или корзиной,
 * и без такой плашки он о нём узнает только зайдя в профиль.
 *
 * Сам алерт живёт несколько секунд, а точка в профиле остаётся до тех пор,
 * пока чат не открыт, — так клиент дойдёт до него и позже.
 */
const LIFE = 8000

const chatStore = useChatStore()
const router = useRouter()
const { t } = useI18n()

let hide: ReturnType<typeof setTimeout> | undefined

watch(
  () => chatStore.notice,
  (notice) => {
    clearTimeout(hide)
    if (!notice) return

    haptic.impact('medium')
    hide = setTimeout(chatStore.dismissNotice, LIFE)
  },
)

onBeforeUnmount(() => clearTimeout(hide))

/** Нажатие — тот же диплинк, что и в списке обращений: сразу нужная комната. */
function open(): void {
  const roomId = chatStore.notice?.roomId
  chatStore.dismissNotice()

  if (roomId) void router.push(`${ROUTE.chat}/${roomId}`)
}
</script>

<template>
  <Teleport to="body">
    <Transition name="notice">
      <div v-if="chatStore.notice" class="notice" role="alert">
        <button type="button" class="notice__body" @click="open">
          <span class="notice__icon">
            <AppIcon name="messages" :size="24" />
          </span>
          <span class="notice__text">
            <span class="notice__title font-14-b">{{ t('chat.noticeTitle') }}</span>
            <span v-if="chatStore.notice.text" class="notice__preview font-13-m">
              {{ chatStore.notice.text }}
            </span>
            <span class="notice__action font-13-b">{{ t('chat.noticeOpen') }}</span>
          </span>
        </button>
        <button
          type="button"
          class="notice__close"
          :aria-label="t('common.close')"
          @click="chatStore.dismissNotice"
        >
          <AppIcon name="x" :size="14" />
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.notice {
  position: fixed;
  top: calc(var(--screen-padding) + env(safe-area-inset-top, 0px));
  inset-inline: var(--screen-padding);
  z-index: var(--z-toast);
  display: flex;
  align-items: flex-start;
  gap: 6px;
  max-width: calc(var(--app-max-width) - var(--screen-padding) * 2);
  margin-inline: auto;
  padding: 10px;
  border-radius: var(--radius-xl);
  background: var(--surface-card);
  box-shadow: var(--shadow-card);

  &__body {
    display: flex;
    flex: 1;
    gap: 10px;
    min-width: 0;
    padding: 0;
    border: 0;
    background: none;
    text-align: left;
    cursor: pointer;
  }

  &__icon {
    display: flex;
    flex: 0 0 42px;
    align-items: center;
    justify-content: center;
    height: 42px;
    border-radius: var(--radius-l);
    background: var(--success-500);
    color: var(--white);
  }

  &__text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  /* Превью — одна строка: плашка не должна закрывать пол-экрана */
  &__preview {
    overflow: hidden;
    color: var(--text-color-secondary);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Действие — обычным текстом: красный здесь читался как ошибка */
  &__action {
    color: var(--text-color);
  }

  &__close {
    display: flex;
    flex: 0 0 24px;
    align-items: center;
    justify-content: center;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: var(--radius-round);
    background: var(--surface-muted);
    color: var(--icon-color);
    cursor: pointer;
  }
}

.notice-enter-active,
.notice-leave-active {
  transition:
    opacity 0.2s,
    transform 0.2s;
}

.notice-enter-from,
.notice-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
